/**
 * Request-ID interceptor for Supabase client.
 *
 * Patches the global fetch so that every request to the Supabase API
 * automatically gets an X-Request-ID header and a corresponding Sentry tag.
 *
 * Call `installRequestIdInterceptor()` once at app startup (e.g. in main.tsx).
 */
import * as Sentry from '@sentry/react';
import { generateRequestId } from '@/lib/request-id';

let installed = false;

export function installRequestIdInterceptor(): void {
  if (installed) return;
  installed = true;

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl) return;

  const originalFetch = globalThis.fetch;

  globalThis.fetch = function patchedFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url;

    // Only attach to requests aimed at our Supabase project
    if (url.startsWith(supabaseUrl)) {
      const requestId = generateRequestId();
      const headers = new Headers(init?.headers);
      headers.set('X-Request-ID', requestId);

      Sentry.getCurrentScope()?.setTag('request_id', requestId);

      return originalFetch(input, { ...init, headers });
    }

    return originalFetch(input, init);
  };
}
